// ===================================
// AI CONFIGURATION CONTEXT
// src/contexts/AIConfigContext.tsx
// ===================================

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import api from "../services/api";
import {
  AIProvider,
  AIMode,
  EffectiveAIConfig,
  UserAIPreferences,
  BusinessAIConfig,
  AIProviderTestResult,
  MessageOverrides,
} from "../types";

// ===================================
// CONTEXT TYPES
// ===================================

export interface AIConfigContextType {
  // Available options
  llmProviders: AIProvider[];
  embeddingProviders: AIProvider[];
  modes: AIMode[];

  // Current configuration
  effectiveConfig: EffectiveAIConfig | null;
  businessConfig: BusinessAIConfig | null;
  userPreferences: UserAIPreferences | null;

  // Permission flags
  canOverride: boolean;
  canUseCustomKeys: boolean;
  hasCustomKey: boolean;

  // Legacy aliases for permission flags (backward compatibility)
  businessAllowsOverride: boolean;
  businessAllowsApiKeys: boolean;

  // Session overrides (per-chat)
  sessionOverrides: MessageOverrides;
  setSessionOverrides: (overrides: MessageOverrides) => void;
  clearSessionOverrides: () => void;

  // Loading states
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;

  // Actions
  refreshConfig: () => Promise<void>;
  updateUserPreferences: (
    prefs: Partial<UserAIPreferences> & { openai_api_key?: string }
  ) => Promise<void>;
  updateBusinessConfig: (config: Partial<BusinessAIConfig>) => Promise<void>;
  testProvider: (
    provider: string,
    model?: string,
    apiKey?: string
  ) => Promise<AIProviderTestResult>;
  setCustomApiKey: (apiKey: string) => Promise<void>;
  removeCustomApiKey: () => Promise<void>;
  removeUserApiKey: () => Promise<void>; // Alias for removeCustomApiKey

  // Helpers
  getProviderModels: (providerKey: string) => string[];
  getModelsForProvider: (providerKey: string) => string[]; // Alias
  getModeInfo: (modeKey: string) => AIMode | undefined;
  isProviderConfigured: (providerKey: string) => boolean;
  getProviderDisplayName: (providerKey: string) => string;
  getModelDisplayName: (providerKey: string, modelKey: string) => string;
}

// ===================================
// DEFAULT VALUES
// ===================================

const defaultModes: AIMode[] = [
  {
    key: "CHAT_ONLY",
    displayName: "Chat Only",
    description: "Direct LLM conversation without document context",
    icon: "💬",
    rag: false,
    agentic: false,
    chat: true,
  },
  {
    key: "RAG_ONLY",
    displayName: "RAG Only",
    description: "Document-based responses only",
    icon: "📚",
    rag: true,
    agentic: false,
    chat: false,
  },
  {
    key: "RAG_CHAT",
    displayName: "RAG + Chat",
    description: "Document-augmented conversations",
    icon: "📖",
    rag: true,
    agentic: false,
    chat: true,
  },
  {
    key: "AGENTIC_ONLY",
    displayName: "Agentic Only",
    description: "Action-based interactions only",
    icon: "🤖",
    rag: false,
    agentic: true,
    chat: false,
  },
  {
    key: "FULL",
    displayName: "Full Mode",
    description: "All capabilities: Chat, RAG, and Actions",
    icon: "🚀",
    rag: true,
    agentic: true,
    chat: true,
  },
];

// Provider display name mapping
const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  openai: "OpenAI",
  "claude-bedrock": "Claude (Bedrock)",
  anthropic: "Anthropic",
  azure: "Azure OpenAI",
  cohere: "Cohere",
  huggingface: "Hugging Face",
};

// ===================================
// CONTEXT CREATION
// ===================================

const AIConfigContext = createContext<AIConfigContextType | undefined>(
  undefined
);

// ===================================
// CUSTOM HOOK
// ===================================

export const useAIConfig = (): AIConfigContextType => {
  const context = useContext(AIConfigContext);
  if (!context) {
    throw new Error("useAIConfig must be used within AIConfigProvider");
  }
  return context;
};

// ===================================
// PROVIDER COMPONENT
// ===================================

interface AIConfigProviderProps {
  children: ReactNode;
}

export const AIConfigProvider: React.FC<AIConfigProviderProps> = ({
  children,
}) => {
  // State
  const [llmProviders, setLlmProviders] = useState<AIProvider[]>([]);
  const [embeddingProviders, setEmbeddingProviders] = useState<AIProvider[]>(
    []
  );
  const [modes, setModes] = useState<AIMode[]>(defaultModes);
  const [effectiveConfig, setEffectiveConfig] =
    useState<EffectiveAIConfig | null>(null);
  const [businessConfig, setBusinessConfig] =
    useState<BusinessAIConfig | null>(null);
  const [userPreferences, setUserPreferences] =
    useState<UserAIPreferences | null>(null);
  const [canOverride, setCanOverride] = useState(true);
  const [canUseCustomKeys, setCanUseCustomKeys] = useState(true);
  const [hasCustomKey, setHasCustomKey] = useState(false);
  const [sessionOverrides, setSessionOverrides] = useState<MessageOverrides>(
    {}
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ===================================
  // DATA FETCHING
  // ===================================

  const fetchProviders = useCallback(async () => {
    try {
      const response = await api.get("/ai-config/providers");
      setLlmProviders(response.data.llm_providers || []);
      setEmbeddingProviders(response.data.embedding_providers || []);
      if (response.data.modes?.length > 0) {
        setModes(response.data.modes);
      }
    } catch (err: any) {
      console.error("Failed to fetch providers:", err);
      // Use defaults on error
    }
  }, []);

  const fetchEffectiveConfig = useCallback(async () => {
    try {
      const response = await api.get("/ai-config/effective");
      setEffectiveConfig(response.data.config);
    } catch (err: any) {
      console.error("Failed to fetch effective config:", err);
    }
  }, []);

  const fetchBusinessConfig = useCallback(async () => {
    try {
      const response = await api.get("/ai-config/business");
      setBusinessConfig(response.data.config);
    } catch (err: any) {
      console.error("Failed to fetch business config:", err);
    }
  }, []);

  const fetchUserPreferences = useCallback(async () => {
    try {
      const response = await api.get("/ai-config/user");
      setUserPreferences(response.data.preferences || null);
      setCanOverride(response.data.business_allows_override ?? true);
      setCanUseCustomKeys(response.data.business_allows_api_keys ?? true);
      setHasCustomKey(response.data.has_custom_api_key ?? false);
    } catch (err: any) {
      console.error("Failed to fetch user preferences:", err);
    }
  }, []);

  const refreshConfig = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await Promise.all([
        fetchProviders(),
        fetchEffectiveConfig(),
        fetchBusinessConfig(),
        fetchUserPreferences(),
      ]);
    } catch (err: any) {
      setError(err.message || "Failed to load AI configuration");
    } finally {
      setIsLoading(false);
    }
  }, [
    fetchProviders,
    fetchEffectiveConfig,
    fetchBusinessConfig,
    fetchUserPreferences,
  ]);

  // Initial load
  useEffect(() => {
    refreshConfig();
  }, [refreshConfig]);

  // ===================================
  // ACTIONS
  // ===================================

  const updateUserPreferences = useCallback(
    async (prefs: Partial<UserAIPreferences> & { openai_api_key?: string }) => {
      setIsUpdating(true);
      try {
        const response = await api.put("/ai-config/user", prefs);
        setUserPreferences(response.data.preferences);
        setHasCustomKey(response.data.has_custom_api_key ?? hasCustomKey);
        // Refresh effective config
        await fetchEffectiveConfig();
      } catch (err: any) {
        throw new Error(
          err.response?.data?.error?.message || "Failed to update preferences"
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchEffectiveConfig, hasCustomKey]
  );

  const updateBusinessConfig = useCallback(
    async (config: Partial<BusinessAIConfig>) => {
      setIsUpdating(true);
      try {
        const response = await api.put("/ai-config/business", config);
        setBusinessConfig(response.data.config);
        // Refresh effective config
        await fetchEffectiveConfig();
      } catch (err: any) {
        throw new Error(
          err.response?.data?.error?.message ||
            "Failed to update business config"
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchEffectiveConfig]
  );

  const testProvider = useCallback(
    async (
      provider: string,
      model?: string,
      apiKey?: string
    ): Promise<AIProviderTestResult> => {
      try {
        // If model not provided, get default model for provider
        const actualModel =
          model ||
          llmProviders.find((p) => p.provider_key === provider)?.config
            ?.default_model ||
          "";

        const response = await api.post("/ai-config/test-llm", {
          provider,
          model: actualModel,
          api_key: apiKey,
        });
        return response.data;
      } catch (err: any) {
        return {
          success: false,
          provider,
          model: model || "",
          error: err.response?.data?.error || err.message,
        };
      }
    },
    [llmProviders]
  );

  const setCustomApiKey = useCallback(
    async (apiKey: string) => {
      await updateUserPreferences({ openai_api_key: apiKey });
    },
    [updateUserPreferences]
  );

  const removeCustomApiKey = useCallback(async () => {
    setIsUpdating(true);
    try {
      await api.delete("/ai-config/user/api-key");
      setHasCustomKey(false);
      await fetchEffectiveConfig();
    } catch (err: any) {
      throw new Error(
        err.response?.data?.error?.message || "Failed to remove API key"
      );
    } finally {
      setIsUpdating(false);
    }
  }, [fetchEffectiveConfig]);

  const clearSessionOverrides = useCallback(() => {
    setSessionOverrides({});
  }, []);

  // ===================================
  // HELPERS
  // ===================================

  const getProviderModels = useCallback(
    (providerKey: string): string[] => {
      const provider = llmProviders.find((p) => p.provider_key === providerKey);
      return provider?.config?.models || [];
    },
    [llmProviders]
  );

  const getModeInfo = useCallback(
    (modeKey: string): AIMode | undefined => {
      return modes.find((m) => m.key === modeKey);
    },
    [modes]
  );

  const isProviderConfigured = useCallback(
    (providerKey: string): boolean => {
      const provider = [...llmProviders, ...embeddingProviders].find(
        (p) => p.provider_key === providerKey
      );
      return provider?.is_enabled ?? false;
    },
    [llmProviders, embeddingProviders]
  );

  const getProviderDisplayName = useCallback(
    (providerKey: string): string => {
      // First check if provider has a display_name
      const provider = llmProviders.find((p) => p.provider_key === providerKey);
      if (provider?.display_name) {
        return provider.display_name;
      }
      // Fall back to mapping
      return PROVIDER_DISPLAY_NAMES[providerKey] || providerKey;
    },
    [llmProviders]
  );

  const getModelDisplayName = useCallback(
    (providerKey: string, modelKey: string): string => {
      // Could be enhanced to have a model display name mapping
      // For now, just return the model key formatted
      return modelKey;
    },
    []
  );

  // ===================================
  // CONTEXT VALUE
  // ===================================

  const value: AIConfigContextType = {
    // Available options
    llmProviders,
    embeddingProviders,
    modes,

    // Current configuration
    effectiveConfig,
    businessConfig,
    userPreferences,

    // Permission flags
    canOverride,
    canUseCustomKeys,
    hasCustomKey,

    // Legacy aliases
    businessAllowsOverride: canOverride,
    businessAllowsApiKeys: canUseCustomKeys,

    // Session overrides
    sessionOverrides,
    setSessionOverrides,
    clearSessionOverrides,

    // Loading states
    isLoading,
    isUpdating,
    error,

    // Actions
    refreshConfig,
    updateUserPreferences,
    updateBusinessConfig,
    testProvider,
    setCustomApiKey,
    removeCustomApiKey,
    removeUserApiKey: removeCustomApiKey, // Alias

    // Helpers
    getProviderModels,
    getModelsForProvider: getProviderModels, // Alias
    getModeInfo,
    isProviderConfigured,
    getProviderDisplayName,
    getModelDisplayName,
  };

  return (
    <AIConfigContext.Provider value={value}>
      {children}
    </AIConfigContext.Provider>
  );
};

// ===================================
// EXPORT
// ===================================

export default AIConfigContext;